<template>
  <div class="text-gray-200">
    <div class="mb-4 text-lg font-medium leading-6">
      {{ isEditMode ? 'Edit Club' : 'Create New Club' }}
    </div>

    <form class="space-y-5" @submit.prevent="handleSubmit">
      <!-- Cover Image -->
      <div>
        <label class="block text-sm font-medium text-white/80 mb-2">
          Cover Image
        </label>
        <div
          class="relative aspect-[16/9] rounded-lg overflow-hidden bg-white/5 border border-white/10 border-dashed">
          <img
            v-if="coverPreview || (isEditMode && props.club?.cover_url)"
            :src="coverPreview || props.club?.cover_url"
            alt="Cover preview"
            class="object-cover w-full h-full" />
          <div
            v-else
            class="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/40">
            <fa-icon :icon="['far', 'image']" class="text-3xl" />
            <span class="text-sm">Add a cover image</span>
          </div>
          <input
            type="file"
            accept="image/*"
            class="absolute inset-0 opacity-0 cursor-pointer"
            @change="handleCoverChange" />
          <!-- Remove button -->
          <button
            v-if="coverPreview || (isEditMode && props.club?.cover_url)"
            type="button"
            class="absolute top-2 right-2 size-8 flex items-center justify-center rounded-full bg-black/60 hover:bg-black/80 text-white/80 hover:text-white transition-colors"
            @click.stop="removeCover">
            <fa-icon :icon="['fas', 'xmark']" class="text-sm" />
          </button>
        </div>
      </div>

      <!-- Club Name -->
      <div>
        <label
          for="club-name"
          class="block text-sm font-medium text-white/80 mb-2">
          Club Name *
        </label>
        <input
          id="club-name"
          v-model="form.name"
          type="text"
          required
          maxlength="100"
          placeholder="e.g., Horror Movie Lovers"
          class="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:border-purple-500 focus:outline-none" />
        <p class="mt-1 text-xs text-white/40">
          {{ form.name.length }}/100 characters
        </p>
      </div>

      <!-- Description -->
      <div>
        <label
          for="club-description"
          class="block text-sm font-medium text-white/80 mb-2">
          Description
        </label>
        <textarea
          id="club-description"
          v-model="form.description"
          rows="3"
          maxlength="500"
          placeholder="What's your club about?"
          class="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:border-purple-500 focus:outline-none resize-none"></textarea>
        <p class="mt-1 text-xs text-white/40">
          {{ form.description.length }}/500 characters
        </p>
      </div>

      <!-- Visibility -->
      <div>
        <label class="block text-sm font-medium text-white/80 mb-3">
          Visibility *
        </label>
        <ClubVisibilityPicker v-model="form.visibility" />
      </div>

      <!-- Join Mode -->
      <div>
        <label class="block text-sm font-medium text-white/80 mb-3">
          How can people join?
        </label>
        <ClubJoinModePicker v-model="form.join_mode" />
      </div>

      <!-- Error message -->
      <div
        v-if="error"
        class="p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-200 text-sm">
        {{ error }}
      </div>

      <!-- Actions -->
      <div class="flex flex-row-reverse gap-2 pt-2">
        <button
          type="submit"
          :disabled="loading || !isValid"
          :class="[
            'px-5 py-2.5 rounded-lg font-medium transition-colors text-sm',
            loading || !isValid
              ? 'bg-white/10 text-white/40 cursor-not-allowed'
              : 'bg-purple-600 text-white hover:bg-purple-500'
          ]">
          <template v-if="loading">
            <fa-icon icon="circle-notch" class="mr-2 animate-spin" />
            {{ isEditMode ? 'Saving...' : 'Creating...' }}
          </template>
          <template v-else>{{
            isEditMode ? 'Save Changes' : 'Create Club'
          }}</template>
        </button>
        <button
          type="button"
          class="px-4 py-2.5 text-sm font-medium text-gray-400 bg-transparent hover:text-white transition-colors"
          @click="closeModal">
          Cancel
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import {
  useStoreClubs,
  CLUB_VISIBILITY,
  CLUB_JOIN_MODE
} from '@/stores/storeClubs'
import { supabase } from '@/supabase'
import { resizeClubCover } from '@/utils/imageResize'
import ClubVisibilityPicker from '@/components/Clubs/ClubVisibilityPicker.vue'
import ClubJoinModePicker from '@/components/Clubs/ClubJoinModePicker.vue'

const props = defineProps({
  club: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close'])

const router = useRouter()
const route = useRoute()
const storeClubs = useStoreClubs()

// Check if we're in edit mode
const isEditMode = computed(() => !!props.club)

// Form state
const form = reactive({
  name: '',
  description: '',
  visibility: CLUB_VISIBILITY.EVERYONE,
  join_mode: CLUB_JOIN_MODE.OPEN
})

const coverFile = ref(null)
const coverPreview = ref(null)
const loading = ref(false)
const error = ref(null)

// Initialize form from club data if editing
const initForm = () => {
  if (props.club) {
    form.name = props.club.name || ''
    form.description = props.club.description || ''
    form.visibility = props.club.visibility || CLUB_VISIBILITY.EVERYONE
    form.join_mode = props.club.join_mode || CLUB_JOIN_MODE.OPEN
    // Set cover preview to existing cover URL if available
    if (props.club.cover_url && !coverFile.value) {
      coverPreview.value = props.club.cover_url
    }
  } else {
    // Reset form for create mode
    form.name = ''
    form.description = ''
    form.visibility = CLUB_VISIBILITY.EVERYONE
    form.join_mode = CLUB_JOIN_MODE.OPEN
    coverFile.value = null
    coverPreview.value = null
  }
}

// Watch for club prop changes
watch(() => props.club, initForm, { immediate: true })
onMounted(() => {
  initForm()
})

// Validation
const isValid = computed(() => {
  return form.name.trim().length >= 3
})

// Handle cover image selection
const handleCoverChange = async (event) => {
  const file = event.target.files?.[0]
  if (!file) return

  // Validate file type
  if (!file.type.startsWith('image/')) {
    error.value = 'Please select an image file'
    return
  }

  try {
    // Resize image before storing
    const resizedFile = await resizeClubCover(file)
    coverFile.value = resizedFile
    coverPreview.value = URL.createObjectURL(resizedFile)
    error.value = null
  } catch (err) {
    console.error('Error resizing image:', err)
    error.value = 'Failed to process image'
  }
}

// Remove cover image
const removeCover = () => {
  // Revoke blob URL if it's a preview
  if (coverPreview.value && coverPreview.value.startsWith('blob:')) {
    URL.revokeObjectURL(coverPreview.value)
  }
  coverFile.value = null
  coverPreview.value = null
  // In edit mode, setting to null will mark it for removal
}

// Upload cover image
const uploadCover = async () => {
  if (!coverFile.value) {
    // If editing and no new file, return existing cover_url or null
    if (isEditMode.value) {
      // If coverPreview was cleared, return null to remove it
      return coverPreview.value === null && !coverFile.value
        ? null
        : props.club?.cover_url
    }
    return null
  }

  const fileExt = coverFile.value.name.split('.').pop()
  const fileName = `club-covers/${Date.now()}.${fileExt}`

  const { error: uploadError } = await supabase.storage
    .from('media')
    .upload(fileName, coverFile.value, {
      cacheControl: '3600',
      upsert: false
    })

  if (uploadError) {
    console.error('[ModalClubCreate] Upload error:', uploadError)
    throw new Error(
      'Failed to upload cover image. Please ensure the storage bucket exists.'
    )
  }

  const {
    data: { publicUrl }
  } = supabase.storage.from('media').getPublicUrl(fileName)

  return publicUrl
}

const closeModal = () => {
  emit('close')
}

// Form submission
const handleSubmit = async () => {
  if (!isValid.value || loading.value) return

  loading.value = true
  error.value = null

  try {
    // Upload cover if provided or changed
    let coverUrl = null
    if (
      coverFile.value ||
      (isEditMode.value && coverPreview.value === null && props.club?.cover_url)
    ) {
      coverUrl = await uploadCover()
    } else if (isEditMode.value) {
      coverUrl = props.club?.cover_url
    }

    if (isEditMode.value) {
      // Update club
      const result = await storeClubs.updateClub(props.club.slug, {
        name: form.name.trim(),
        description: form.description.trim() || null,
        visibility: form.visibility,
        join_mode: form.join_mode,
        cover_url: coverUrl
      })

      if (!result) {
        error.value = storeClubs.error || 'Failed to update club'
        return
      }

      // Close modal
      closeModal()

      // If slug changed, navigate to new URL
      if (result.slug !== props.club.slug) {
        router.replace({ name: 'WebClubDetail', params: { slug: result.slug } })
      }
    } else {
      // Create club
      const club = await storeClubs.createClub({
        name: form.name.trim(),
        description: form.description.trim() || null,
        visibility: form.visibility,
        join_mode: form.join_mode,
        cover_url: coverUrl
      })

      if (!club) {
        error.value = storeClubs.error || 'Failed to create club'
        return
      }

      // Close modal and navigate to the new club
      closeModal()
      router.push({ name: 'WebClubDetail', params: { slug: club.slug } })
    }
  } catch (err) {
    console.error('[ModalClubCreate] Error:', err)
    error.value =
      err.message ||
      (isEditMode.value ? 'Failed to update club' : 'Failed to create club')
  } finally {
    loading.value = false
  }
}
</script>
